import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { useContext } from 'react';
import type Result from 'true-myth/result';

import { ApplicationContext } from '~/shared/context/context';
import type { Pathname } from '~/shared/http-fetcher/http-fetcher';

import type { BusinessIntelligenceOptions } from '../../data-contract/business-intelligence';
import { businessIntelligenceOptionsSchema } from '../../data-contract/business-intelligence';

export const useBusinessIntelligenceOptions = (): UseQueryResult<Result<BusinessIntelligenceOptions, Error>> => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const pathname: Pathname = `/business-intelligence/options`;

    return useQuery({
        queryFn: () =>
            validatingCobrainerApiFetcher.get(businessIntelligenceOptionsSchema, {
                pathname,
            }),
        queryKey: [pathname, 'GET'],
    });
};

const defaultOptions = {
    canReadSkillGapTimeline: false,
    canReadStatusOverview: false,
    canReadTrendingJobs: false,
    canReadTrendingSkills: false,
    canReadUserVisibilities: false,
} as const;

export const useBusinessIntelligencePermissions = (): {
    readonly canAccessBusinessIntelligence: boolean;
    readonly isPending: boolean;
} => {
    const businessIntelligenceOptions = useBusinessIntelligenceOptions();

    const isPending = businessIntelligenceOptions.isPending;

    const {
        canReadSkillGapTimeline,
        canReadStatusOverview,
        canReadTrendingJobs,
        canReadTrendingSkills,
        canReadUserVisibilities,
    } = businessIntelligenceOptions.isSuccess
        ? businessIntelligenceOptions.data.unwrapOr(defaultOptions)
        : defaultOptions;

    const canAccessBusinessIntelligence =
        canReadSkillGapTimeline ||
        canReadStatusOverview ||
        canReadTrendingJobs ||
        canReadTrendingSkills ||
        canReadUserVisibilities;

    return {
        canAccessBusinessIntelligence,
        isPending,
    };
};
